import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import Card from "../../components/ui/Card.jsx";
import { Lock, Loader2, CheckCircle2, ShieldCheck, AlertCircle, Eye, EyeOff } from "lucide-react";
import { verifyAndSetPassword } from "../../api/borrowerPortal/borrowerSignUp.js";

/**
 * Normalize API response/error into a single display message.
 */
function getMessage(responseOrError) {
  if (!responseOrError || typeof responseOrError !== "object") return null;
  const data = responseOrError.data != null ? responseOrError.data : responseOrError;
  if (typeof data !== "object") return null;

  const msg = data.message;
  if (Array.isArray(msg) && msg.length > 1 && typeof msg[1] === "string") return msg[1].trim();
  if (typeof msg === "string" && msg.trim()) return msg.trim();

  const exc = data.exc;
  if (typeof exc === "string" && exc.trim()) return exc.trim();

  return null;
}

export default function VerifyAccount() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
const key = decodeURIComponent(searchParams.get("key") || "");
const email = decodeURIComponent(searchParams.get("email") || "");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const hasValidParams = key.trim() !== "" && email.trim() !== "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    setLoading(true);
    try {
      await verifyAndSetPassword({
        key: key.trim(),
        email: email.trim().toLowerCase(),
        new_password: password,
      });
      setSuccess(true);
      setTimeout(() => {
        navigate("/borrower/signin", { replace: true });
      }, 2000);
    } catch (err) {
      const errData = err?.response?.data ?? err?.data ?? err;
      const message = getMessage(errData) || err?.message || "Verification failed. Please try again or request a new link.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:grid md:grid-cols-2 bg-slate-50">
      {/* Left branding panel (Desktop only) */}
      <div className="hidden md:flex relative items-center overflow-hidden pl-10">
        <div className="absolute inset-0 bg-gradient-to-b from-sky-500 to-sky-600" />
        <div className="relative z-10 text-white max-w-xl">
          <div className="flex items-center gap-2">
            <a href="/">
              <img
                src="/dynesourcer-logo.svg"
                alt="Dynesourcer"
                className="h-64 md:h-96 select-none hover:opacity-90 transition-opacity"
                draggable="false"
                loading="eager"
                width="384"
                height="384"
              />
            </a>
            <div>
              <h1 className="text-4xl font-extrabold">Dynesourcer</h1>
              <p className="text-white/80">Borrower Application Portal</p>
            </div>
          </div>
          <div className="mt-10">
            <h2 className="text-3xl font-bold">Verify your email</h2>
            <p className="mt-2 text-white/85">
              Set your password to activate your account and sign in.
            </p>
          </div>
        </div>
      </div>

      {/* Mobile header */}
      <div className="md:hidden bg-gradient-to-b from-sky-500 to-sky-600 px-6 py-6 text-white">
        <div className="flex items-center gap-3 mb-4">
          <a href="/">
            <img
              src="/dynesourcer-logo.svg"
              alt="Dynesourcer"
              className="h-24 w-auto"
              draggable="false"
              width="96"
              height="96"
            />
          </a>
          <div>
            <h1 className="text-xl font-extrabold">Dynesourcer</h1>
            <p className="text-white/85 text-xs">Borrower Portal</p>
          </div>
        </div>
        <h2 className="text-lg font-bold">Verify your email</h2>
        <p className="text-white/85 text-sm mt-1">Set your password to activate your account.</p>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-4 sm:p-6 md:p-6 min-h-0 flex-1 overflow-y-auto">
        <Card className="w-full max-w-md rounded-2xl bg-white p-4 sm:p-6 shadow-sm mx-auto">
          {!hasValidParams ? (
            <div className="py-6 text-center">
              <div className="rounded-full bg-rose-100 p-4 mb-4 inline-flex" aria-hidden>
                <AlertCircle className="h-12 w-12 text-rose-600" />
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-slate-900">Invalid verification link</h2>
              <p className="mt-3 text-sm text-slate-600">
                This link is missing the verification key or email. Please use the link from your verification email, or request a new one from the sign-up page.
              </p>
              <Link
                to="/borrower/signup"
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl bg-sky-600 px-4 py-3 text-sm font-semibold text-white hover:bg-sky-700 transition-colors"
              >
                Go to Sign up
              </Link>
            </div>
          ) : success ? (
            <div className="flex flex-col items-center text-center py-6">
              <div className="rounded-full bg-emerald-100 p-4 mb-4" aria-hidden>
                <CheckCircle2 className="h-12 w-12 text-emerald-600" />
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-slate-900">Password set</h2>
              <p className="mt-3 text-sm text-slate-700">
                Your account is verified. Redirecting you to sign in...
              </p>
            </div>
          ) : (
            <>
              <div className="mb-4 sm:mb-6">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-sky-500" />
                  <h2 className="text-lg sm:text-xl font-semibold text-slate-900">Set your password</h2>
                </div>
                <p className="mt-1 text-xs sm:text-sm text-slate-600">
                  Verifying: <span className="font-medium text-slate-800">{email}</span>
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                <label className="block">
                  <span className="mb-1 block text-xs sm:text-sm font-medium text-slate-700">New password</span>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`w-full rounded-xl border px-3 py-2.5 pr-12 text-sm sm:text-base focus:ring-2 focus:ring-sky-200 ${
                        error ? "border-slate-300" : "border-slate-300"
                      }`}
                      placeholder="At least 8 characters"
                      disabled={loading}
                      autoComplete="new-password"
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 rounded focus:outline-none focus:ring-2 focus:ring-sky-200"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </label>

                <label className="block">
                  <span className="mb-1 block text-xs sm:text-sm font-medium text-slate-700">Confirm password</span>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`w-full rounded-xl border px-3 py-2.5 pr-12 text-sm sm:text-base focus:ring-2 focus:ring-sky-200 ${
                        error && password !== confirmPassword ? "border-rose-300" : "border-slate-300"
                      }`}
                      placeholder="Re-enter your password"
                      disabled={loading}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 rounded focus:outline-none focus:ring-2 focus:ring-sky-200"
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </label>

                {error && (
                  <p className="text-xs sm:text-sm text-rose-600 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-sky-500 px-4 py-2.5 text-sm sm:text-base font-semibold text-white hover:bg-sky-600 disabled:opacity-70 transition-colors"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Lock className="h-4 w-4" />
                  )}
                  {loading ? "Setting password..." : "Set password & verify"}
                </button>
              </form>

              <p className="mt-4 text-center text-xs sm:text-sm text-slate-600">
                Remember your password?{" "}
                <Link to="/borrower/signin" className="font-semibold text-sky-700 hover:underline">
                  Sign in
                </Link>
              </p>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
